import { loadStripe } from "@stripe/stripe-js";
import React from "react";
import { BiCartAdd } from "react-icons/bi";
import { FaMoneyCheck } from "react-icons/fa";
import { FiAlertCircle } from "react-icons/fi";
import { ThreeDots } from "react-loader-spinner";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import { GetParameter } from "src/assets/functions";
import { useDialog } from "src/contexts/dialog-context";
import PlansOver from "src/modules/TrainerCheckout";
import api from "src/rest/api";
import { twMerge } from "tailwind-merge";
import HistoryItemComponent, { HistoryItem } from "../BillingHistoryItem";

interface SubscriptionData {
  id: string;
  plan: {
    id: string;
    amount: number;
    currency: string;
  };
  current_period_end: string;
  auto_renew: boolean;
}

const CurrentPlan: React.FC = () => {
  const { data, isLoading } = useQuery("userplan", () =>
    api
      .post<{ data?: SubscriptionData; history: HistoryItem[] }>(
        "/stripe/verify",
      )
      .then((res) => res.data)
      .catch(() => {
        alert("Não foi possível carregar informações de faturamento!");
      }),
  );

  const preview = GetParameter("preview");

  const dialog = useDialog();

  const nav = useNavigate();

  function ActivateOverlay() {
    nav("/settings?preview=true");
  }

  function RequestCancel() {
    dialog.openDialog(
      "Cancelar Assinatura",
      "Para cancelar a assinatura BETA, entre em contato com os Administradores da plataforma!",
      undefined,
      "OK",
      true,
    );
  }

  function OpenChangeIntent() {
    api
      .post<{ session: any; publishableKey: string }>("/stripe/payment-method")
      .then(async (res) => {
        try {
          const stripe = await loadStripe(res.data.publishableKey);

          if (!stripe) {
            throw new Error("Falha ao carregar o Stripe.");
          }

          await stripe.redirectToCheckout({
            sessionId: res.data.session.id,
          });
        } catch {
          dialog.openDialog(
            "Erro",
            "Não foi possível abrir a página de alteração de método de pagamento.",
            undefined,
            "OK",
            true,
          );
        }
      });
  }

  if (isLoading) {
    return (
      <div className="settings-card flex min-h-[300px] items-center justify-center rounded-xl bg-white shadow-md">
        <ThreeDots height={40} color="#444" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="settings-card flex min-h-[300px] flex-col items-center justify-center rounded-xl bg-white shadow-md">
        <FiAlertCircle className="mb-4 text-5xl text-red-500" />
        <p className="text-lg font-semibold text-red-600">
          Erro ao carregar dados!
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col">
      {preview !== undefined && <PlansOver />}

      <div className="w-full rounded-t-lg bg-zinc-100 dark:bg-zinc-850 md:p-4">
        <h4 className="mb-3 text-lg font-semibold text-zinc-800 dark:text-zinc-100">
          Plano Base
        </h4>

        <div className="p-2 md:py-3">
          {data.data ? (
            <div className="grid grid-cols-1 gap-2 text-zinc-700 dark:text-zinc-200 md:grid-cols-1">
              <div>
                <strong className="block text-xs font-bold uppercase text-zinc-500 dark:text-zinc-400">
                  Valor
                </strong>
                <span className="text-base">
                  {data.data.plan.amount}{" "}
                  {data.data.plan.currency.toUpperCase()}
                </span>
              </div>
              <div>
                <strong className="block text-xs font-bold uppercase text-zinc-500 dark:text-zinc-400">
                  Próximo ciclo
                </strong>
                <span className="text-base">
                  {data.data.current_period_end &&
                    new Date(data.data.current_period_end).toLocaleDateString()}
                </span>
              </div>
              <div>
                <strong className="block text-xs font-bold uppercase text-zinc-500 dark:text-zinc-400">
                  Renovação automática
                </strong>
                <span
                  className={`text-base font-semibold ${data.data.auto_renew ? "text-green-600 dark:text-green-400" : "text-red-500 dark:text-red-400"}`}
                >
                  {data.data.auto_renew ? "Ativa" : "Inativa"}
                </span>
              </div>
              <>
                <div className="flex flex-col items-center gap-4 pt-2 md:flex-row md:justify-between">
                  <button
                    onClick={OpenChangeIntent}
                    className="w-full rounded-lg border border-zinc-500 bg-zinc-100 px-3 py-1 font-semibold text-zinc-600 transition hover:bg-zinc-200 dark:border-zinc-400 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800 md:w-auto"
                  >
                    Alterar Método de Pagamento
                  </button>
                  <button
                    onClick={RequestCancel}
                    className="px-3 py-1 font-semibold text-red-500 transition hover:opacity-75 dark:text-red-400 md:w-auto"
                  >
                    Cancelar Assinatura
                  </button>
                </div>
              </>
            </div>
          ) : (
            <div
              onClick={ActivateOverlay}
              className="group flex cursor-pointer flex-col items-center justify-center py-3 text-center"
            >
              <FiAlertCircle
                className="mb-2 text-red-500 dark:text-red-500"
                size={20}
              />
              <p className="mt-2 font-semibold text-red-500 dark:text-red-400">
                Você não possui uma assinatura ativa.
              </p>
              <button
                className={twMerge(
                  // Layout, size, and font
                  "mt-4 flex items-center justify-center gap-2 rounded-lg px-5 py-2.5 text-center font-bold text-white shadow-lg transition-all duration-300 ease-in-out focus:outline-none",
                  // Gradient background and hover/active states
                  "bg-gradient-to-r from-primary-500 to-primary-700 group-hover:from-primary-600 group-hover:to-primary-800 dark:from-primary-600 dark:to-primary-800",
                  // Ring and shadow effects
                  "ring-2 ring-transparent group-hover:bg-primary-700 group-hover:shadow-[0_0_16px_4px_theme('colors.primary.500')] group-hover:ring-primary-500",
                  // Drop shadow and scale on hover/active
                  "group-hover:ring-primary-400/50 group-hover:ring-offset-1 group-hover:ring-offset-primary-500 group-hover:drop-shadow-[0_0_12px_rgba(255,0,30,1)]",
                  "active:ring-4 active:ring-primary-500/60 active:ring-offset-1 active:ring-offset-white active:drop-shadow-[0_0_18px_rgba(59,130,246,0.7)]",
                  // Disabled state (if needed, you can add a prop to control this)
                  // "cursor-not-allowed bg-zinc-600 text-zinc-400": !plan.amount,
                )}
              >
                <BiCartAdd className="text-2xl" />
                <span>Assine Já</span>
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="w-full rounded-b-lg bg-zinc-150 dark:bg-zinc-900 md:p-4">
        <h4 className="mb-3 text-lg font-semibold text-zinc-800 dark:text-zinc-100">
          Histórico de Faturamento
        </h4>
        <div className="rounded-md bg-zinc-50 p-4 dark:bg-zinc-800 md:p-6">
          <ul className="flex flex-col gap-2">
            {data.history && data.history.length > 0 ? (
              data.history.slice(0, 5).map((e, i) => (
                <li
                  key={"hs_" + i}
                  className="flex items-center justify-between border-b py-2 last:border-0 dark:border-zinc-700"
                >
                  <HistoryItemComponent item={e} />
                </li>
              ))
            ) : (
              <div className="flex flex-col items-center justify-center py-3 text-center">
                <FaMoneyCheck className="mb-2 text-4xl text-zinc-400 dark:text-zinc-200" />
                <p className="mt-2 font-semibold text-zinc-500 dark:text-zinc-200">
                  Você não possui histórico de transações!
                </p>
              </div>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default CurrentPlan;
